/**
 * Approved-sender index.
 *
 * The display-name warning ("this name claims a different address") is only
 * useful if the user can silence it for senders they know. Rather than invent
 * a second store, this reuses the existing shared allowlist — the same
 * senders/domains lists that drive remote-image loading, synced via GDrive
 * (Bob 2026-08-20: "another variation on approved senders. If a message is
 * flagged I should be able to mark it as valid").
 *
 * Consequence worth knowing: the lists are shared, so marking a sender
 * legitimate also permits their remote images. That is the intended coupling
 * — one notion of "I trust this sender" — but it is a real side effect.
 *
 * Loaded once at startup and refreshed after any write. Reads are synchronous
 * and hot (once per rendered row), so they must never touch IPC.
 */
import { getAllowlist } from "./api-client.js";

let senders = new Set<string>();
let domains = new Set<string>();
let loaded = false;

const norm = (s: string): string => (s || "").trim().toLowerCase();
const domainOf = (addr: string): string => norm(addr).split("@").pop() || "";

/** Refresh from the daemon. Safe to call repeatedly; failures leave the
 *  previous index in place rather than clearing it (a transient IPC error
 *  must not make every approved sender light up again). */
export async function refreshApprovedSenders(): Promise<void> {
    try {
        const list = await getAllowlist();
        senders = new Set((list.senders || []).map(norm));
        domains = new Set((list.domains || []).map(norm));
        loaded = true;
    } catch (e: any) {
        console.warn("[approved-senders] refresh failed:", e?.message || e);
    }
}

/** Has the user vouched for this sender, by address or by whole domain?
 *  Returns false until the first load completes — a not-yet-loaded index
 *  shows the warning rather than silently trusting everyone. */
export function isSenderApproved(address: string): boolean {
    if (!loaded) return false;
    const a = norm(address);
    if (!a) return false;
    if (senders.has(a)) return true;
    const d = domainOf(a);
    return !!d && domains.has(d);
}

/** Optimistic local add so the UI updates before the write round-trips. */
export function noteApprovedLocally(type: "sender" | "domain", value: string): void {
    const v = norm(value);
    if (!v) return;
    if (type === "sender") senders.add(v); else domains.add(v);
    loaded = true;
}

export function approvedCounts(): { senders: number; domains: number; loaded: boolean } {
    return { senders: senders.size, domains: domains.size, loaded };
}
